import {
  type ColumnFiltersState,
  createColumnHelper,
  type RowSelectionState,
  type SortingState,
  type VisibilityState,
} from '@tanstack/react-table';
import { format } from 'date-fns';
import { useMemo, useState } from 'react';
import type { DateRange } from 'react-day-picker';

import {
  type PendingPayment,
  useGetPendingPayments,
} from '~entities/payment/api/get-pending-payments';

import { ApprovePendingPayment } from '~features/payment/ui/payment-processing/approve-pending-payment';

import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '~/shared/shadcn/ui/card';
import {
  EnrollioTable,
  EnrollioTableColumnToggle,
} from '~shared/components/enrollio-table';
import { DataTableColumnHeader } from '~shared/components/table';

import { DateRangePicker } from '../date-range-picker';

const columnHelper = createColumnHelper<PendingPayment>();

export function ManualPendingReview() {
  const [date, setDate] = useState<DateRange | undefined>({
    from: new Date(),
    to: new Date(),
  });
  const [sorting, setSorting] = useState<SortingState>([]);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({});
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});

  const { data, isFetching } = useGetPendingPayments({
    startDate: date?.from ? format(new Date(date.from), 'yyyy-MM-dd') : '',
    endDate: date?.to ? format(new Date(date.to), 'yyyy-MM-dd') : '',
  });

  const columns = useMemo(
    () => [
      columnHelper.accessor('date', {
        header: (info) => (
          <DataTableColumnHeader
            disableHiding
            className='pl-4'
            column={info.column}
            title='Date'
          />
        ),
        cell: (info) => (
          <div className='px-4'>
            {info.getValue()} ({info.row.original.pending} pending)
          </div>
        ),
      }),
      columnHelper.display({
        id: 'action',
        header: () => <div className=''>Actions</div>,
        cell: (info) => <ApprovePendingPayment date={info.row.original.date} />,
      }),
    ],
    [],
  );

  return (
    <Card>
      <CardHeader>
        <CardTitle>Pending Manual Review</CardTitle>
        <CardDescription>
          Review and process payments that require manual verification, grouped
          by date
        </CardDescription>
      </CardHeader>
      <CardContent>
        <EnrollioTable
          ariaLabel='Pending manual payment reviews'
          totalLabel='Review dates'
          filterHeader={<DateRangePicker date={date} setDate={setDate} />}
          toolbar={(table) => <EnrollioTableColumnToggle table={table} />}
          columnFilters={columnFilters}
          columnVisibility={columnVisibility}
          columns={columns}
          data={data ?? []}
          enableRowSelection={true}
          isUpdating={isFetching}
          onColumnFiltersChange={setColumnFilters}
          onColumnVisibilityChange={setColumnVisibility}
          onRowSelectionChange={setRowSelection}
          onSortingChange={setSorting}
          rowSelection={rowSelection}
          showSelectionCount
          sorting={sorting}
        />
      </CardContent>
    </Card>
  );
}
